/**
 * pricingPlans
 *
 * Centralized pricing plan data and helpers used across the app.
 */
import { kevinMeetingUrl } from '../scheduling/schedulingLinks';

// Plan IDs (used for API interactions)
export const PLAN_IDS = {
  PREMIUM: 'premium',
  ENTERPRISE: 'enterprise'
};

// Plan tier IDs (used for UI components)
export const TIER_IDS = {
  PREMIUM: 'tier-premium',
  ENTERPRISE: 'tier-enterprise'
};

// Plan frequencies
export const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual', badge: '30% off' }
];

// Plan features
export const PLAN_FEATURES = {
  [PLAN_IDS.PREMIUM]: [
    '500+ editable templates',
    '100+ stock videos',
    'Video templates',
    'Mail templates',
    'Gather supporter videos from your community',
    'Ad copy analyzer',
  ],
  [PLAN_IDS.ENTERPRISE]: [
    'Everything in Premium, plus:',
    'Single invoice for your organization',
    'Custom company branding on your accounts',
    'Dedicated account manager',
    'Priority development of requested features'
  ]
};

// Plan pricing
export const PLAN_PRICING = {
  [PLAN_IDS.PREMIUM]: { 
    monthly: '$34', 
    annual: '$24'  // per month, billed annually ($288/year)
  },
  [PLAN_IDS.ENTERPRISE]: {
    monthly: 'Custom',
    annual: 'Custom'
  }
};

// Original pricing (for showing crossed-out prices)
export const ORIGINAL_PRICING = {
  [PLAN_IDS.PREMIUM]: { 
    monthly: '$49', 
    annual: '$34'
  }
};

// Discount information
export const PLAN_DISCOUNTS = {
  [PLAN_IDS.PREMIUM]: {
    percentage: 30,
    label: '30% off for limited time'
  },
  [PLAN_IDS.ENTERPRISE]: {
    percentage: 30,
    label: '30% off for limited time'
  }
};

// Discount percentages for annual plans
export const ANNUAL_DISCOUNT = {
  [PLAN_IDS.PREMIUM]: 30,  // 30% savings with annual ($34 to $24)
  [PLAN_IDS.ENTERPRISE]: 0 // No discount shown for enterprise
};

// Total annual cost (for display purposes)
export const ANNUAL_TOTAL = {
  [PLAN_IDS.PREMIUM]: '$288', // $24 × 12
  [PLAN_IDS.ENTERPRISE]: 'Custom pricing'
};

// Main pricing tiers data - Updated structure
export const PRICING_TIERS = [
  {
    name: 'Premium',
    id: TIER_IDS.PREMIUM,
    planId: PLAN_IDS.PREMIUM,
    href: '/pricing',
    featured: true,
    description: 'Complete creative suite with all features to save you significant time and money.',
    price: PLAN_PRICING[PLAN_IDS.PREMIUM],
    highlights: PLAN_FEATURES[PLAN_IDS.PREMIUM],
  },
  {
    name: 'Enterprise',
    id: TIER_IDS.ENTERPRISE,
    planId: PLAN_IDS.ENTERPRISE,
    href: '/pricing',
    featured: false,
    isEnterprise: true,
    description: 'Custom solutions for organizations with dedicated support and branding.',
    price: PLAN_PRICING[PLAN_IDS.ENTERPRISE],
    highlights: PLAN_FEATURES[PLAN_IDS.ENTERPRISE],
    contactInfo: {
      calendlyUrl: kevinMeetingUrl,
      buttonText: 'Contact Sales'
    }
  },
];

/**
 * Helper functions for pricing plans
 */

// Get display name for a plan
export function getPlanDisplayName(planId) {
  if (planId === 'basic' || planId === 'standard') return 'Basic';
  if (planId === PLAN_IDS.PREMIUM) return 'Premium';
  if (planId === PLAN_IDS.ENTERPRISE) return 'Enterprise';
  return planId.charAt(0).toUpperCase() + planId.slice(1);
}

// Get price for a plan
export function getPlanPrice(planId, frequencyValue = 'monthly') {
  const pricing = PLAN_PRICING[planId];
  if (!pricing) return '';
  return pricing[frequencyValue];
}

// Get formatted price with period
export function getFormattedPrice(planId, frequencyValue = 'annual') {
  if (planId === 'basic') return 'Free';
  if (planId === PLAN_IDS.ENTERPRISE) return 'Custom/month';
  
  if (frequencyValue === 'annual') {
    if (planId === PLAN_IDS.PREMIUM) return '$24/month';
  } else {
    if (planId === PLAN_IDS.PREMIUM) return '$34/month';
  }
  
  return '';
}

// Get interval display name
export function getIntervalDisplayName(interval) {
  if (!interval) return '';
  if (interval === 'monthly') return 'Monthly';
  if (interval === 'annual') return 'Annual';
  return interval.charAt(0).toUpperCase() + interval.slice(1);
}

// Get features for a specific plan
export function getPlanFeatures(planId) {
  return PLAN_FEATURES[planId] || [];
}

// Get the billing explanation text
export function getBillingText(planId, frequencyValue = 'annual') {
  if (planId === PLAN_IDS.ENTERPRISE) return 'Discounted pricing for more customers';
  
  if (frequencyValue === 'annual') {
    return `billed annually (${ANNUAL_TOTAL[planId]}/year)`;
  }
  return 'billed monthly';
}

// Get discount text for annual plans
export function getDiscountText(planId) {
  if (planId === PLAN_IDS.ENTERPRISE) return 'Custom pricing based on your requirements';
  return `Save ${ANNUAL_DISCOUNT[planId]}% with annual billing`;
}

// Default export with all pricing data
export default {
  frequencies: FREQUENCIES,
  tiers: PRICING_TIERS,
  planIds: PLAN_IDS,
  planFeatures: PLAN_FEATURES,
  planPricing: PLAN_PRICING,
  originalPricing: ORIGINAL_PRICING,
  planDiscounts: PLAN_DISCOUNTS,
  getPlanDisplayName,
  getPlanPrice,
  getFormattedPrice,
  getIntervalDisplayName,
  getPlanFeatures,
  getBillingText,
  getDiscountText
}; 